// packages/utils/src/normalize.ts

/**
 * Normalize a city/place name for deterministic matching:
 *   - NFD decomposition + strip combining marks (accent removal)
 *   - Lowercase
 *   - Trim outer whitespace
 *   - Collapse internal whitespace to single spaces
 *
 * Mirrors the backend `normalize()` helper used in LocationsService and
 * the location seeder, so client-side lookups produce the same key.
 */
export function normalizeCityName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/** Uppercase display text with pt-BR accents, preserving its whitespace.
 * Identity storage uses normalizeName for NFC and canonical spacing.
 * Missing values become an empty form value.
 */
export function toUpperCasePtBR(value: string | null | undefined): string {
  if (value == null) return '';
  return value.toLocaleUpperCase('pt-BR');
}

/**
 * Accent-insensitive keys and generic text whose contract removes accents.
 * Mirrors the backend toUpperNoAccents helper. Identity fields use
 * normalizeName; display labels use toUpperCasePtBR when uppercase is needed.
 * `null`/`undefined` devolvem string vazia.
 */
export function toUpperNoAccents(value: string | null | undefined): string {
  if (value == null) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleUpperCase('pt-BR')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Identity storage mirror: NFC, uppercase pt-BR, accents and normalized spaces.
 * Missing values become an empty form value; backend preserves null/undefined.
 * Editing controls should call this after composition or on submit.
 */
export function normalizeName(value: string | null | undefined): string {
  if (value == null) return '';
  return value
    .normalize('NFC')
    .replace(/[\s\p{White_Space}]+/gu, ' ')
    .trim()
    .toLocaleUpperCase('pt-BR')
    .normalize('NFC');
}

/** Canonical email identity: preserve dots, plus tags and internal characters for validation. */
export function normalizeEmail(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}
